import "@/index.css";
import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import CookieBanner from "@/components/CookieBanner";
import HomePage from "@/pages/HomePage";
import { readConsent, applyConsent, DEFAULT_CONSENT } from "@/lib/consent";

// Lazy-load secondary pages — shrinks the initial JS bundle so the homepage paints faster.
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ManWithAVanDublin = lazy(() => import("@/pages/seo/ManWithAVanDublin"));
const HouseRemovalsDublin = lazy(() => import("@/pages/seo/HouseRemovalsDublin"));
const FurnitureDeliveryDublin = lazy(() => import("@/pages/seo/FurnitureDeliveryDublin"));
const ApartmentMovesDublin = lazy(() => import("@/pages/seo/ApartmentMovesDublin"));
const LocationPage = lazy(() => import("@/pages/seo/LocationPage"));
const ServiceSeoPage = lazy(() => import("@/pages/seo/ServiceSeoPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const BlogIndexPage = lazy(() => import("@/pages/blog/BlogIndexPage"));
const BlogPostPage = lazy(() => import("@/pages/blog/BlogPostPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const LandlordsPage = lazy(() => import("@/pages/LandlordsPage"));
const MovingCleaningPackagesPage = lazy(() => import("@/pages/MovingCleaningPackagesPage"));

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </>
  );
}

// Minimal fallback while a lazy chunk loads. No spinner = no extra paint.
function PageFallback() {
  return <div className="min-h-screen" aria-hidden="true" />;
}

function App() {
  // On mount, replay any existing consent choice to third-party tools so PostHog
  // and Google Ads pick up the previously-granted permissions (or stay denied).
  useEffect(() => {
    const stored = readConsent();
    applyConsent(stored || DEFAULT_CONSENT);
  }, []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen flex flex-col">
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
              <Route path="/pricing" element={<PublicLayout><PricingPage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/man-with-a-van-dublin" element={<PublicLayout><ManWithAVanDublin /></PublicLayout>} />
              <Route path="/house-removals-dublin" element={<PublicLayout><HouseRemovalsDublin /></PublicLayout>} />
              <Route path="/furniture-delivery-dublin" element={<PublicLayout><FurnitureDeliveryDublin /></PublicLayout>} />
              <Route path="/apartment-moves-dublin" element={<PublicLayout><ApartmentMovesDublin /></PublicLayout>} />
              <Route path="/man-with-a-van-dublin-6" element={<PublicLayout><LocationPage areaKey="dublin-6" /></PublicLayout>} />
              <Route path="/man-with-a-van-dublin-8" element={<PublicLayout><LocationPage areaKey="dublin-8" /></PublicLayout>} />
              <Route path="/man-with-a-van-dublin-12" element={<PublicLayout><LocationPage areaKey="dublin-12" /></PublicLayout>} />
              <Route path="/man-with-a-van-lucan" element={<PublicLayout><LocationPage areaKey="lucan" /></PublicLayout>} />
              <Route path="/man-with-a-van-tallaght" element={<PublicLayout><LocationPage areaKey="tallaght" /></PublicLayout>} />
              <Route path="/man-with-a-van-blanchardstown" element={<PublicLayout><LocationPage areaKey="blanchardstown" /></PublicLayout>} />
              <Route path="/man-with-a-van-clondalkin" element={<PublicLayout><LocationPage areaKey="clondalkin" /></PublicLayout>} />
              <Route path="/man-with-a-van-swords" element={<PublicLayout><LocationPage areaKey="swords" /></PublicLayout>} />
              <Route path="/man-with-a-van-dundrum" element={<PublicLayout><LocationPage areaKey="dundrum" /></PublicLayout>} />
              <Route path="/man-with-a-van-sandyford" element={<PublicLayout><LocationPage areaKey="sandyford" /></PublicLayout>} />
              <Route path="/man-with-a-van-rathmines" element={<PublicLayout><LocationPage areaKey="rathmines" /></PublicLayout>} />
              <Route path="/man-with-a-van-castleknock" element={<PublicLayout><LocationPage areaKey="castleknock" /></PublicLayout>} />
              <Route path="/man-with-a-van-blackrock" element={<PublicLayout><LocationPage areaKey="blackrock" /></PublicLayout>} />
              <Route path="/junk-removal-dublin" element={<PublicLayout><ServiceSeoPage pageKey="junk-removal-dublin" /></PublicLayout>} />
              <Route path="/office-removals-dublin" element={<PublicLayout><ServiceSeoPage pageKey="office-removals-dublin" /></PublicLayout>} />
              <Route path="/storage-removals-dublin" element={<PublicLayout><ServiceSeoPage pageKey="storage-removals-dublin" /></PublicLayout>} />
              <Route path="/apartment-removals-dublin" element={<PublicLayout><ServiceSeoPage pageKey="apartment-removals-dublin" /></PublicLayout>} />
              <Route path="/same-day-removals-dublin" element={<PublicLayout><ServiceSeoPage pageKey="same-day-removals-dublin" /></PublicLayout>} />
              <Route path="/furniture-disposal-dublin" element={<PublicLayout><ServiceSeoPage pageKey="furniture-disposal-dublin" /></PublicLayout>} />
              <Route path="/waste-collection-dublin" element={<PublicLayout><ServiceSeoPage pageKey="waste-collection-dublin" /></PublicLayout>} />
              <Route path="/rubbish-removal-dublin" element={<PublicLayout><ServiceSeoPage pageKey="rubbish-removal-dublin" /></PublicLayout>} />
              <Route path="/moving-service-dublin" element={<PublicLayout><ServiceSeoPage pageKey="moving-service-dublin" /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><BlogIndexPage /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
              <Route path="/cookies" element={<PublicLayout><CookiePolicyPage /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
              <Route path="/landlords-estate-agents" element={<PublicLayout><LandlordsPage /></PublicLayout>} />
              <Route path="/moving-cleaning-packages" element={<PublicLayout><MovingCleaningPackagesPage /></PublicLayout>} />
              <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" />
        </div>
        <WhatsAppButton />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
